import {
  BellRing,
  Database,
  Download,
  ExternalLink,
  Image,
  LockKeyhole,
  Mail,
  ShieldCheck,
  Smartphone,
  Trash2,
  UserRound,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Seo, breadcrumbSchema } from '../lib/seo';

const updatedDate = 'July 24, 2026';

const sections = [
  {
    id: 'scope',
    icon: Smartphone,
    title: '1. Scope of this policy',
    paragraphs: [
      'This Privacy Policy applies to the Focora mobile application provided by Nexhance AI. Focora is a personal focus timer, habit tracker and notes app designed to work without creating an account.',
      'The Nexhance AI website has a separate website privacy policy. When you open a Focora support or privacy page in your browser, the website policy also applies to that web visit.',
    ],
  },
  {
    id: 'local-data',
    icon: Database,
    title: '2. Information stored on your device',
    paragraphs: [
      'Focora stores the information you enter locally on your device so that your routines and preferences remain available between app launches.',
    ],
    bullets: [
      'Display name, selected role, optional work or study field, and selected interests.',
      'Habits, schedules, reminder times, timer durations and completion history.',
      'Focus sessions, custom focus activities, weekly goals and progress statistics.',
      'Notes you create inside the app.',
      'Theme, sound, vibration, motion and other app preferences.',
    ],
  },
  {
    id: 'photos',
    icon: Image,
    title: '3. Profile photos',
    paragraphs: [
      'Focora requests photo-library permission only after you choose to add or change a profile picture. The app creates a resized local copy for your profile. Focora does not upload that picture to a Focora server.',
      'You can remove the profile picture from Settings. Deleting all app data also removes the saved local copy managed by the app.',
    ],
  },
  {
    id: 'notifications',
    icon: BellRing,
    title: '4. Notifications, sounds and vibration',
    paragraphs: [
      'With your permission, Focora schedules habit reminders, daily planning reminders and timer-completion alerts through your device operating system. The current version does not use a Focora remote-push server.',
      'Your chosen alarm sound, notification sound and vibration preferences are stored locally. Operating-system settings such as Focus modes, silent mode, notification permissions and battery restrictions can affect delivery.',
    ],
  },
  {
    id: 'sharing',
    icon: Download,
    title: '5. Exporting and sharing data',
    paragraphs: [
      'The Export my data feature creates a readable report from information stored by Focora on your device. The app opens the system share sheet so you can choose where to save or send it.',
      'Focora does not automatically send exported information anywhere. After you choose another app, cloud service or person from the share sheet, their privacy terms apply to that copy.',
    ],
  },
  {
    id: 'collection',
    icon: LockKeyhole,
    title: '6. Analytics, advertising and tracking',
    paragraphs: [
      'The current version of Focora does not include third-party advertising, behavioural analytics, cross-app tracking or a Focora account system. Focora does not sell personal information.',
      'The app is built with React Native and Expo components that help it access normal device features such as local storage, notifications, audio and the photo picker. Focora does not intentionally use those components to create an advertising profile or track you across other companies’ apps and websites.',
    ],
  },
  {
    id: 'support',
    icon: Mail,
    title: '7. Support communications',
    paragraphs: [
      'When you contact support, Nexhance AI receives the email address, message, attachments and technical details you choose to provide. We use that information to respond, troubleshoot the issue, prevent abuse and maintain a reasonable support record.',
      'Do not send sensitive personal information that is not necessary to explain the problem. Support messages may be retained while needed to resolve the request and for reasonable security or legal purposes.',
    ],
  },
  {
    id: 'retention',
    icon: Trash2,
    title: '8. Retention and deletion',
    paragraphs: [
      'Local app information remains on your device until you edit it, use Delete all data, remove the profile picture, clear the app’s storage, or uninstall Focora. Focora cannot restore locally deleted information unless you previously exported and saved your own copy.',
      'You can revoke photo or notification permission at any time from iOS or iPadOS Settings. You can also disable reminders, sounds and vibration inside Focora Settings.',
    ],
  },
  {
    id: 'children',
    icon: UserRound,
    title: '9. Children’s privacy',
    paragraphs: [
      'Focora is a general productivity app and is not directed to children under 13. A parent or guardian can remove locally stored information by using Delete all data or uninstalling the app.',
    ],
  },
  {
    id: 'security',
    icon: ShieldCheck,
    title: '10. Security, changes and contact',
    paragraphs: [
      'Focora is designed to keep its working data on the device, but no device or software environment can be guaranteed completely secure. Protect your device with an appropriate passcode and keep iOS or iPadOS updated.',
      'We may update this policy when Focora changes. The revised date will appear at the top of this page, and material changes will be reflected in the app or release information before they apply.',
      'Privacy questions can be sent to info@nexhanceai.com.',
    ],
  },
];

export function FocoraPrivacyPage() {
  return (
    <section className="page-section focora-public-page">
      <Seo
        title="Focora Privacy Policy"
        description="Privacy Policy for the Focora focus timer, habit tracker and notes app, including local storage, profile photos, reminders, exports and data deletion."
        path="/focora/privacy"
        noindex
        jsonLd={[
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Focora', path: '/focora/support' },
            { name: 'Privacy Policy', path: '/focora/privacy' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Focora Privacy Policy',
            url: 'https://nexhanceai.com/focora/privacy',
            dateModified: '2026-07-24',
            publisher: { '@type': 'Organization', name: 'Nexhance AI', url: 'https://nexhanceai.com/' },
          },
        ]}
      />

      <div className="shell focora-public-shell">
        <header className="focora-public-hero">
          <div className="focora-app-mark" aria-hidden="true"><ShieldCheck size={30} /></div>
          <div className="focora-hero-copy">
            <p className="eyebrow">Focora app · Legal</p>
            <h1>Privacy Policy</h1>
            <p className="focora-hero-summary">
              A clear explanation of what Focora stores, what stays on your device, and the controls available to you.
            </p>
            <div className="focora-meta-row">
              <span>Last updated: {updatedDate}</span>
              <span>Developer: Nexhance AI</span>
            </div>
          </div>
        </header>

        <div className="focora-policy-layout">
          <aside className="focora-policy-nav" aria-label="Privacy Policy sections">
            <strong>On this page</strong>
            {sections.map((section) => (
              <a key={section.id} href={`#${section.id}`}>{section.title.replace(/^\d+\.\s*/, '')}</a>
            ))}
          </aside>

          <div className="focora-policy-content">
            <div className="focora-privacy-highlight">
              <LockKeyhole size={22} />
              <div>
                <strong>Designed around local storage</strong>
                <p>Focora does not require an account, does not include ads or behavioural analytics, and does not automatically upload your habits, focus history, notes or profile picture to a Focora server.</p>
              </div>
            </div>

            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <section key={section.id} id={section.id} className="focora-policy-section">
                  <div className="focora-section-icon" aria-hidden="true"><Icon size={20} /></div>
                  <div>
                    <h2>{section.title}</h2>
                    {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                    {section.bullets ? <ul>{section.bullets.map((item) => <li key={item}>{item}</li>)}</ul> : null}
                  </div>
                </section>
              );
            })}

            <div className="focora-contact-panel">
              <div>
                <p className="eyebrow">Need clarification?</p>
                <h2>Contact Focora support</h2>
                <p>We normally reply within one to two business days.</p>
              </div>
              <a className="button button-primary" href="mailto:info@nexhanceai.com?subject=Focora%20Privacy%20Question">
                <Mail size={17} /> Email support
              </a>
            </div>

            <div className="focora-related-links">
              <Link to="/focora/support">Focora Support <ExternalLink size={14} /></Link>
              <Link to="/privacy">Nexhance AI website privacy policy <ExternalLink size={14} /></Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
